import { useMemo, useState, useEffect, useCallback } from 'react';

// Hooks
import useSubscriptionClick from '../useSubscriptionClick';
import useFetchIntervalData from '../useFetchIntervalData';

// Utils
import dayjs from 'dayjs';
import { getISODate } from '../../../apps/basketball/src/utils/getISODate';
import { getValidatedDate } from '@sportal365/smp-widget-utils/src/date';
import { getMultiSportProgrammeEvents } from '../../services/multi-sport/events';
import { stackOdds } from '@sportal365/smp-widget-utils/src/oddsCounter';
import { normalizeEventsData } from '@sportal365/smp-widget-normalizers/src/multi-sport/normalizeEventsResponseData';

// Types
import { IMultiSportEventsResponse, SDKOptions } from '../../types';
import { IMultiSportTeamProgrammeConfig, INormalizedEvents } from '../../types/multisport';
import { IGeneralSubscriberInfo } from '../../constants/subscribeTypes';

// Constants
import { FINISHED, INTERRUPTED, LIVE, NOT_STARTED, UNKNOWN } from '../../constants/statusTypes';
import { FIXTURES, PROGRAMME_MATCH_TYPE } from '../../constants/programmeStatus';

interface IUseTeamProgrammeArgs extends IGeneralSubscriberInfo {
  filter: string;
  dataLimit?: IMultiSportTeamProgrammeConfig['dataLimit'];
  dataDateFrom?: IMultiSportTeamProgrammeConfig['dataDateFrom'];
  dataDateTo?: IMultiSportTeamProgrammeConfig['dataDateTo'];
  dataRefreshTime?: IMultiSportTeamProgrammeConfig['dataRefreshTime'];
  dataTeam?: IMultiSportTeamProgrammeConfig['dataTeam'];
  dataSeason?: IMultiSportTeamProgrammeConfig['dataSeason'];
  dataStage?: IMultiSportTeamProgrammeConfig['dataStage'];
  dataRoundsFilter?: IMultiSportTeamProgrammeConfig['dataRoundsFilter'];
  dataRounds?: IMultiSportTeamProgrammeConfig['dataRounds'];
  dataMatchType?: IMultiSportTeamProgrammeConfig['dataMatchType'];
  dataSortDirectionFixtures?: IMultiSportTeamProgrammeConfig['dataSortDirectionFixtures'];
  dataSortDirectionResults?: IMultiSportTeamProgrammeConfig['dataSortDirectionResults'];
  dataTeamNameType?: IMultiSportTeamProgrammeConfig['dataTeamNameType'];
  dataEntityLinks?: IMultiSportTeamProgrammeConfig['dataEntityLinks'];
  dataLabels?: IMultiSportTeamProgrammeConfig['dataLabels'];
  dataOddsDisplay?: IMultiSportTeamProgrammeConfig['dataOddsDisplay'];
  dataOddsMarketValueType?: IMultiSportTeamProgrammeConfig['dataOddsMarketValueType'];
  dataOddsBettingId?: IMultiSportTeamProgrammeConfig['dataOddsBettingId'];
  dataOddsMarket?: IMultiSportTeamProgrammeConfig['dataOddsMarket'];
  dataOddsPreEventOnly?: IMultiSportTeamProgrammeConfig['dataOddsPreEventOnly'];
  sdkOptions: SDKOptions;
}

const filterMatchesByType = (data: INormalizedEvents[], dataTeam?: string, dataMatchType?: string) => {
  return data.map((competition) => ({
    ...competition,
    events: competition.events.filter((event) => {
      if (dataMatchType === PROGRAMME_MATCH_TYPE) {
        return event.participants[0]?.id === dataTeam;
      }
      return event.participants[1]?.id === dataTeam;
    }),
  }));
};

export const useTeamProgramme = ({
  subscribers,
  dataWidgetId,
  dataWidgetSport,
  dataRefreshTime,
  filter,
  dataTeam,
  dataSeason,
  dataStage,
  dataRoundsFilter,
  dataRounds,
  dataMatchType,
  dataTeamNameType,
  dataEntityLinks,
  dataLabels,
  dataLimit,
  dataDateFrom,
  dataDateTo,
  dataSortDirectionFixtures,
  dataSortDirectionResults,
  dataOddsDisplay,
  dataOddsMarketValueType,
  dataOddsMarket,
  dataOddsBettingId,
  dataOddsPreEventOnly,
  dataWidgetCuid,
  sdkOptions,
}: IUseTeamProgrammeArgs) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fixtures, setFixtures] = useState<INormalizedEvents[]>();
  const [hasFixtures, setHasFixtures] = useState<boolean | undefined>();
  const [results, setResults] = useState<INormalizedEvents[]>();
  const [hasResults, setHasResults] = useState<boolean | undefined>();

  const startDate = useMemo(() => {
    const validDate = getValidatedDate(dayjs, dataDateFrom?.date, dataDateFrom?.dateFormat);

    return validDate && getISODate(validDate, sdkOptions.dataConfigTimezone, true);
  }, [dataDateFrom, sdkOptions]);

  const endDate = useMemo(() => {
    const validDate = getValidatedDate(dayjs, dataDateTo?.date, dataDateTo?.dateFormat);

    return validDate && getISODate(validDate, sdkOptions.dataConfigTimezone, false);
  }, [dataDateTo, sdkOptions]);

  const round_filter = useMemo(() => {
    if (dataRounds && dataRounds.length > 0 && dataStage) {
      return dataRounds.map((round) => `${dataStage}:${round}`);
    }

    const filters = dataRoundsFilter
      ?.map(({ stageId, roundId }) => (stageId && roundId ? `${stageId}:${roundId}` : ''))
      .filter((value) => value !== '');

    return filters && filters.length > 0 ? filters : undefined;
  }, [dataRoundsFilter, dataRounds, dataStage]);

  const params = {
    team_ids: dataTeam,
    season_ids: dataSeason,
    stage_ids: round_filter ? '' : dataStage,
    from_start_time: startDate,
    to_start_time: endDate,
    limit: dataLimit ? dataLimit : '350',
    round_filter: round_filter,
    sport: dataWidgetSport,
    dataOddsDisplay: dataOddsDisplay,
    dataOddsBettingId: dataOddsBettingId,
    dataOddsMarket: dataOddsMarket,
    dataOddsMarketValueType: dataOddsMarketValueType,
    dataOddsPreEventOnly: dataOddsPreEventOnly,
  };

  const fetchTeamProgramme = useCallback(
    (manageLoader: boolean, isFromInterval = false) => {
      manageLoader && setIsLoading(true);
      if (dataTeam && dataSeason) {
        // FIXTURES
        getMultiSportProgrammeEvents({
          params: {
            ...params,
            status_type: `${NOT_STARTED},${LIVE},${INTERRUPTED},${UNKNOWN}`,
            sort_order: dataSortDirectionFixtures?.toUpperCase(),
          },
          sdkOptions,
        })
          ?.then(({ competitions }: IMultiSportEventsResponse) => {
            const hasGames = competitions ? competitions.some(({ events }) => events.length > 0) : undefined;
            const fixtures = normalizeEventsData({
              competitions,
              nameType: dataTeamNameType,
              dataLabels,
              dataEntityLinks,
              sdkOptions,
            });
            const filteredFixtures = dataMatchType && dataTeam ? filterMatchesByType(fixtures, dataTeam, dataMatchType) : fixtures;
            setHasFixtures(hasGames);
            setFixtures(filteredFixtures);
            !isFromInterval && stackOdds(competitions, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
          })
          .catch((error: unknown) => {
            console.error(error);
          });

        // RESULTS
        getMultiSportProgrammeEvents({
          params: { ...params, status_type: FINISHED, sort_order: dataSortDirectionResults?.toUpperCase() },
          sdkOptions,
        })
          ?.then(({ competitions }: IMultiSportEventsResponse) => {
            manageLoader && setIsLoading(false);
            const hasGames = competitions ? competitions.some(({ events }) => events.length > 0) : undefined;
            const results = normalizeEventsData({
              competitions,
              nameType: dataTeamNameType,
              dataLabels,
              dataEntityLinks,
              sdkOptions,
            });
            const filteredResults = dataMatchType && dataTeam ? filterMatchesByType(results, dataTeam, dataMatchType) : results;
            setHasResults(hasGames);
            setResults(filteredResults);
            !isFromInterval && stackOdds(competitions, dataWidgetSport, dataWidgetId, dataWidgetCuid, dataOddsBettingId);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [
      dataTeam,
      dataLimit,
      dataSeason,
      startDate,
      endDate,
      round_filter,
      dataMatchType,
      dataTeamNameType,
      dataOddsBettingId,
      dataOddsDisplay,
      dataOddsMarketValueType,
      dataOddsMarket,
      dataOddsPreEventOnly,
      dataSeason,
      dataSortDirectionFixtures,
      dataSortDirectionResults,
      dataStage,
      dataWidgetCuid,
      dataWidgetId,
      dataWidgetSport,
    ],
  );

  useFetchIntervalData(fetchTeamProgramme, dataRefreshTime);

  useEffect(() => {
    fetchTeamProgramme(true);
  }, [fetchTeamProgramme]);

  return {
    widgetData: { games: filter === FIXTURES ? fixtures : results, hasFixtures, hasResults, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
};
